import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AssistantSelectors } from './AssistantSelectors';
import { useLoginContext } from '../login/LoginHandler';
import { useErrorContext } from '../../contexts/ErrorContext';
import { useAssistantLoaders } from '../../hooks/useAssistantLoaders';
import { useCurrentThreadContext } from '../../contexts/CurrentThreadContext';
import { PromptConfigOption } from '../../types/PromptConfigOption';
import { AssistantDefinition } from '../../client/SmartAssistantBackendClient';

export function AgentSelection() {
  const consumer = useLoginContext();
  const { setError } = useErrorContext();
  const { agentSelection, setAgentSelection } = useCurrentThreadContext();

  const [assistants, setAssistants] = useState<AssistantDefinition[]>([]);
  const [documentAnswerPromptConfigs, setDocumentAnswerPromptConfigs] = useState<PromptConfigOption[]>([]);
  const [selectedAssistantInternalName, setSelectedAssistantInternalName] = useState<string>(agentSelection.assistantInternalName);
  const [selectedDocumentAnswerPromptConfigKey, setSelectedDocumentAnswerPromptConfigKey] = useState<string>(agentSelection.promptConfigKey ?? '');
  const [assistantPromptsError, setAssistantPromptsError] = useState<string | null>(null);
  const [includeProfile, setIncludeProfile] = useState<boolean>(agentSelection.includeProfile);

  const selectedAssistantInternalNameRef = React.useRef<string>('');
  const selectedDocumentAnswerPromptConfigKeyRef = React.useRef<string>('');
  const assistantsLoadedRef = React.useRef<boolean>(false);
  const assistantsLoadPromiseRef = React.useRef<Promise<void> | null>(null);
  const assistantsRetryRef = React.useRef<number>(0);
  const MAX_AUTO_RETRIES = 10;

  const { loadAssistants, loadAssistantPrompts } = useAssistantLoaders({
    assistantsLoadedRef,
    assistantsLoadPromiseRef,
    assistantsRetryRef,
    selectedAssistantInternalNameRef,
    selectedDocumentAnswerPromptConfigKeyRef,
    setAssistantPromptsError,
    setAssistants,
    setDocumentAnswerPromptConfigs,
    setSelectedAssistantInternalName,
    setSelectedDocumentAnswerPromptConfigKey,
    setError,
    MAX_AUTO_RETRIES,
  });

  useEffect(() => {
    if (assistantsLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      const token = await consumer.getUserToken();
      if (cancelled) return;
      void loadAssistants(token as string);
    })();
    return () => { cancelled = true; };
  }, [consumer, loadAssistants]);

  useEffect(() => {
    const assistantInternalName = selectedAssistantInternalName;
    if (!assistantInternalName) {
      setDocumentAnswerPromptConfigs([]);
      setSelectedDocumentAnswerPromptConfigKey('');
      selectedDocumentAnswerPromptConfigKeyRef.current = '';
      setAssistantPromptsError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const token = await consumer.getUserToken();
      if (cancelled) return;
      await loadAssistantPrompts(token as string, assistantInternalName);
    })();
    return () => { cancelled = true; };
  }, [consumer, loadAssistantPrompts, selectedAssistantInternalName]);

  useEffect(() => {
    setAgentSelection({
      assistantInternalName: selectedAssistantInternalName,
      promptConfigKey: selectedDocumentAnswerPromptConfigKey || null,
      includeProfile,
    });
  }, [includeProfile, selectedAssistantInternalName, selectedDocumentAnswerPromptConfigKey, setAgentSelection]);

  const isAssistantSelected = Boolean(selectedAssistantInternalName);
  const effectiveAwaitingResponse = false;

  const handleAssistantChange = useCallback((next: string) => {
    setSelectedAssistantInternalName(next);
  }, []);

  const handlePromptConfigChange = useCallback((next: string) => {
    setSelectedDocumentAnswerPromptConfigKey(next);
  }, []);

  const handleIncludeProfileChange = useCallback((next: boolean) => {
    setIncludeProfile(next);
  }, []);

  const assistantSelectorsProps = useMemo(() => ({
    assistants,
    selectedAssistantInternalName,
    onAssistantChange: handleAssistantChange,
    documentAnswerPromptConfigs,
    selectedDocumentAnswerPromptConfigKey,
    onPromptConfigChange: handlePromptConfigChange,
    assistantPromptsError,
    includeProfile,
    onIncludeProfileChange: handleIncludeProfileChange,
    effectiveAwaitingResponse,
    isAssistantSelected,
  }), [
    assistants,
    selectedAssistantInternalName,
    handleAssistantChange,
    documentAnswerPromptConfigs,
    selectedDocumentAnswerPromptConfigKey,
    handlePromptConfigChange,
    assistantPromptsError,
    includeProfile,
    handleIncludeProfileChange,
    effectiveAwaitingResponse,
    isAssistantSelected,
  ]);

  return <AssistantSelectors {...assistantSelectorsProps} />;
}
